"use client";
import type { RowData } from "@tanstack/react-table";

import { useMemo, useState } from "react";
import { GripVertical, Search, X } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import type { RecordColumnState, RecordObjectDef } from "../types";
import { resolveColumnIds } from "./column-id";

interface EditColumnsSheetProps<TData extends RowData> {
  object: RecordObjectDef<TData>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columns: RecordColumnState;
  onApply: (columns: RecordColumnState) => void;
  onReset: () => void;
}

export function EditColumnsSheet<TData extends RowData>({
  object,
  open,
  onOpenChange,
  columns,
  onApply,
  onReset,
}: EditColumnsSheetProps<TData>) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 sm:max-w-lg"
      >
        <SheetHeader className="border-b border-border">
          <SheetTitle>Edit columns</SheetTitle>
        </SheetHeader>
        {open ? (
          <ColumnsSheetBody
            object={object}
            columns={columns}
            onApply={onApply}
            onReset={onReset}
            onClose={() => onOpenChange(false)}
          />
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function initialOrder(columns: RecordColumnState, allIds: string[]): string[] {
  const known = columns.order.filter((id) => allIds.includes(id));
  return [
    ...known,
    ...allIds.filter(
      (id) => !known.includes(id) && !columns.hidden.includes(id),
    ),
  ];
}

function moveColumn(current: string[], fromId: string, toId: string): string[] {
  const next = current.filter((id) => id !== fromId);
  const target = next.indexOf(toId);
  next.splice(target < 0 ? next.length : target, 0, fromId);
  return next;
}

function useColumnLabels<TData extends RowData>(
  object: RecordObjectDef<TData>,
  allIds: string[],
) {
  return useMemo(() => {
    const map = new Map<string, string>();
    for (const field of object.fields) map.set(field.id, field.label);
    object.columns.forEach((column, index) => {
      const id = allIds[index];
      if (id && !map.has(id)) {
        map.set(id, typeof column.header === "string" ? column.header : id);
      }
    });
    return map;
  }, [object.fields, object.columns, allIds]);
}

function useColumnsDraft<TData extends RowData>(
  object: RecordObjectDef<TData>,
  columns: RecordColumnState,
) {
  const allIds = useMemo(
    () => resolveColumnIds(object.columns),
    [object.columns],
  );
  const [order, setOrder] = useState<string[]>(() =>
    initialOrder(columns, allIds),
  );
  const [hidden, setHidden] = useState<string[]>(() =>
    columns.hidden.filter((id) => allIds.includes(id)),
  );
  const labelOf = useColumnLabels(object, allIds);

  const show = (id: string) => {
    setHidden((current) => current.filter((entry) => entry !== id));
    setOrder((current) => (current.includes(id) ? current : [...current, id]));
  };
  const hide = (id: string) => setHidden((current) => [...current, id]);
  const reorder = (fromId: string, toId: string) => {
    setOrder((current) => moveColumn(current, fromId, toId));
  };

  return { allIds, order, hidden, labelOf, show, hide, reorder };
}

function AvailableColumnsPane({
  hiddenIds,
  labelOf,
  onShow,
}: {
  hiddenIds: string[];
  labelOf: Map<string, string>;
  onShow: (id: string) => void;
}) {
  const [search, setSearch] = useState("");
  const available = hiddenIds.filter((id) =>
    (labelOf.get(id) ?? id).toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="flex min-h-0 flex-col">
      <div className="relative p-3">
        <Search className="pointer-events-none absolute top-1/2 left-5 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search columns…"
          className="pl-7" size="compact"
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
        {available.length === 0 ? (
          <p className="py-6 text-center text-xs text-muted-foreground">
            All columns are shown.
          </p>
        ) : (
          available.map((id) => (
            <label
              key={id}
              className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted"
            >
              <Checkbox checked={false} onCheckedChange={() => onShow(id)} />
              <span className="truncate">{labelOf.get(id) ?? id}</span>
            </label>
          ))
        )}
      </div>
    </div>
  );
}

function SelectedColumnRow({
  label,
  isDragging,
  onDragStart,
  onDragEnd,
  onDrop,
  onHide,
}: {
  label: string;
  isDragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDrop: () => void;
  onHide: () => void;
}) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={(event) => event.preventDefault()}
      onDrop={onDrop}
      className={cn(
        "group/col flex items-center gap-1.5 rounded-md px-1.5 py-1.5 text-sm hover:bg-muted",
        isDragging && "opacity-40",
      )}
    >
      <GripVertical className="h-3.5 w-3.5 shrink-0 cursor-grab text-muted-foreground" />
      <span className="flex-1 truncate">{label}</span>
      <Button
        variant="ghost"
        size="compact"
        className="w-6 p-0 opacity-0 group-hover/col:opacity-100"
        onClick={onHide}
        aria-label={`Remove ${label}`}
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  );
}

function SelectedColumnsPane({
  selected,
  labelOf,
  onHide,
  onReorder,
}: {
  selected: string[];
  labelOf: Map<string, string>;
  onHide: (id: string) => void;
  onReorder: (fromId: string, toId: string) => void;
}) {
  const [dragId, setDragId] = useState<string | null>(null);

  return (
    <div className="flex min-h-0 flex-col">
      <p className="px-3 py-3 text-xs font-medium text-muted-foreground">
        Selected ({selected.length})
      </p>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
        {selected.map((id) => (
          <SelectedColumnRow
            key={id}
            label={labelOf.get(id) ?? id}
            isDragging={dragId === id}
            onDragStart={() => setDragId(id)}
            onDragEnd={() => setDragId(null)}
            onDrop={() => {
              if (dragId && dragId !== id) onReorder(dragId, id);
              setDragId(null);
            }}
            onHide={() => onHide(id)}
          />
        ))}
      </div>
    </div>
  );
}

function ColumnsSheetFooter({
  onReset,
  onApply,
}: {
  onReset: () => void;
  onApply: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2 border-t border-border p-4">
      <Button variant="ghost" size="sm" onClick={onReset}>
        Reset to default
      </Button>
      <Button variant="primary" size="sm" onClick={onApply}>
        Apply
      </Button>
    </div>
  );
}

function ColumnsSheetBody<TData extends RowData>({
  object,
  columns,
  onApply,
  onReset,
  onClose,
}: {
  object: RecordObjectDef<TData>;
  columns: RecordColumnState;
  onApply: (columns: RecordColumnState) => void;
  onReset: () => void;
  onClose: () => void;
}) {
  const { allIds, order, hidden, labelOf, show, hide, reorder } =
    useColumnsDraft(object, columns);

  return (
    <>
      <div className="grid min-h-0 flex-1 grid-cols-2 divide-x divide-border">
        <AvailableColumnsPane
          hiddenIds={allIds.filter((id) => hidden.includes(id))}
          labelOf={labelOf}
          onShow={show}
        />
        <SelectedColumnsPane
          selected={order.filter((id) => !hidden.includes(id))}
          labelOf={labelOf}
          onHide={hide}
          onReorder={reorder}
        />
      </div>

      <ColumnsSheetFooter
        onReset={() => {
          onReset();
          onClose();
        }}
        onApply={() => {
          onApply({ order, hidden, widths: columns.widths });
          onClose();
        }}
      />
    </>
  );
}
